import { useState, useEffect, useMemo, useRef } from 'react';
 
const RECIPE_STORAGE_KEY = 'recipe_cards_data';

export const useRecipeCard = (recipe) => {
  console.log('🎣 useRecipeCard hook called:', recipe?.title);
  
  const [cardData, setCardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const loadingRef = useRef(false);
  const requestInProgressRef = useRef(null);

  // Мемоизируем ключ рецепта
  const recipeKey = useMemo(() => {
    if (!recipe?.title) return null;
    return `${recipe.title}_${recipe.usedIngredients?.join('_')}`;
  }, [recipe?.title, recipe?.usedIngredients]);

  // Получаем данные из sessionStorage
  const getStoredData = () => {
    const stored = sessionStorage.getItem(RECIPE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  };

  // Сохраняем данные в sessionStorage
  const saveToStorage = (key, data) => {
    const currentData = getStoredData();
    const updatedData = { ...currentData, [key]: data };
    sessionStorage.setItem(RECIPE_STORAGE_KEY, JSON.stringify(updatedData));
  };

  useEffect(() => {
    const loadCardData = async () => {
      if (!recipeKey) return;
      if (loadingRef.current) return;
      
      // Проверяем, нет ли уже запроса в процессе
      if (requestInProgressRef.current) {
        console.log('🔄 Request already in progress, cancelling');
        return;
      }

      const storedData = getStoredData();
      if (storedData[recipeKey]) {
        console.log('💾 Found in storage:', recipe?.title);
        setCardData(storedData[recipeKey]);
        return;
      }

      console.log('🚀 Loading card data for:', recipe?.title);
      loadingRef.current = true;
      setIsLoading(true);

      try {
        // Используем изображение, которое уже должно быть в recipe.img
        // Здесь больше не вызываем generateFoodImage
        const imageUrl = recipe.img;
        
        if (!imageUrl) {
          console.warn('⚠️ No image URL provided for:', recipe?.title);
        } else {
          console.log('✅ Using provided image:', recipe?.title, imageUrl?.substring(0, 50) + '...');
        }

        const fullCardData = {
          title: recipe.title,
          cooktime: recipe.cooktime,
          calories: recipe.calories,
          level: recipe.level,
          usedIngredients: recipe.usedIngredients,
          imageUrl,
          timestamp: Date.now()
        };

        saveToStorage(recipeKey, fullCardData);
        setCardData(fullCardData);
      } catch (error) {
        console.error('❌ Error loading card data:', recipe?.title, error);
        setIsLimitReached(true);
      } finally {
        console.log('🏁 Finished loading:', recipe?.title);
        setIsLoading(false);
        loadingRef.current = false;
        requestInProgressRef.current = null;
      }
    };

    loadCardData();

    return () => {
      loadingRef.current = false;
      requestInProgressRef.current = null;
      console.log('🧹 useRecipeCard cleanup:', recipe?.title);
    };
  }, [recipeKey]);

  return { cardData, isLoading, isLimitReached };
}; 